import { useEffect, useState, useCallback } from 'react';
import {
    getCustomerList,
    getCustomerPage,
    getHasMoreCustomers,
    getCustomerLoading,
    getCustomers,
} from './CustomerSlice';
import { toTitleCase } from '../../../Utility/toTitleCase';
import { Button, Card, Container, Spinner } from 'reactstrap';
import { IconButton, InputAdornment, OutlinedInput } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { Customer } from '../../../Types/Customer';
import { useAppDispatch, useAppSelector } from '../../../hooks';
import Loading from '../../../Components/Loading';
import { getWriterId } from '../../SalesDep/Managers/SalesRepView/WriterSlice';
import { Clear, Search } from '@mui/icons-material';
import { useInfiniteScroll } from '../../../Hooks/useInfiniteScroll';

const CustomerReport = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const customers = useAppSelector(getCustomerList) as Customer[];
    const page = useAppSelector(getCustomerPage);
    const hasMore = useAppSelector(getHasMoreCustomers);
    const token = useAppSelector((state) => state.auth.token);
    const currentWriter = useAppSelector(getWriterId)
    const loading = useAppSelector(getCustomerLoading);

    const [query, setQuery] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [searchLoading, setSearchLoading] = useState<boolean>(false);
    const [searchData, setSearchData] = useState<Customer[]>([]);

    useEffect(() => {
        if (currentWriter) {
            dispatch(getCustomers({ writerId: currentWriter }));
        } else {
            dispatch(getCustomers({ }));
        }
    }, [dispatch, currentWriter]);

    const loadMoreData = useCallback(() => {
        if (loading || !hasMore) return;
        if (currentWriter) {
            dispatch(getCustomers({ page: page + 1, writerId: currentWriter }));
        } else {
            dispatch(getCustomers({ page: page + 1 }));
        }
    }, [dispatch, loading, hasMore, page]);

    const handleSearch = (text: string) => {
        setQuery(text);
    };

    const searchCustomer = async (query: string) => {
        try {
            setError(null);
            setSearchLoading(true);
            const baseUrl = 'https://customer.hirsch.com/account/v2/employee/customer';
    
            const res = await fetch(`${baseUrl}?lookup=${query}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': btoa(`Bearer ${token}`)
                },
            });
            const json = await res.json();
            
            if (!res.ok) throw new Error('Failed to Fetch');
            if (!json.ack) throw new Error(json.errorMesssage);
            if (!json.customers[0]) setError('No Results Found')
            setSearchData(json.customers);
        } catch (e: any) {
            setError(e)
        } finally {
            setSearchLoading(false);
        }
    }

    // useEffect(() => {
    //     if (!query) {
    //         setSearchData([]);
    //         return;
    //     }

    //     setSearchData(
    //         customers.filter(
    //             (cust) =>
    //                 cust.id?.toString().includes(query) ||
    //                 cust.name?.toLowerCase().includes(query.toLowerCase()) ||
    //                 cust.contact?.toLowerCase().includes(query.toLowerCase()) ||
    //                 cust.address1?.toLowerCase().includes(query.toLowerCase()) ||
    //                 cust.phone?.toLowerCase().includes(query.toLowerCase())
    //         )
    //     );
    // }, [query, customers]);

    const renderFooter = () => {
        if (!loading || !hasMore) return null;

        return (
            <div style={{ height: 80,}} className='justify-content-center d-flex pt-3'>
                <Spinner 
                    color="primary"
                    style={{ width: '3rem', height: '3rem'}}
                />
            </div>
        );
    };

    const renderItem = ({ item }: { item: Customer }) => {
        return (
            <Link 
                to={`/Customers/${item?.id}`} 
                state={{ item }}
                style={{ textDecoration: 'none'}}
            >
                <Card
                    className='shadow'
                    key={item.id}
                    style={{ marginBottom: 2 }}
                >
                    <div style={{ display: 'flex', gap: 10, padding: 10 }}>
                        <div>
                            <span className='d-flex'>Cust ID:</span>
                            <span className='d-flex'>Cust Name:</span>
                        </div>
                        <div>
                            <span className='d-flex'>{item.id}</span>
                            <span className='d-flex'>{toTitleCase(item.name)}</span>
                        </div>
                    </div>
                </Card>
            </Link>
        )
    };

    const sentinelRef = useInfiniteScroll({
        onEndReached: loadMoreData,
        enabled: hasMore && !loading,
    });

    return (
        <div>
            <div
                style={{
                    position: 'sticky',
                    top: 60,
                    zIndex: 1000,
                    paddingBlock: '10px',
                }}
                className='bg-light'
            >
                <OutlinedInput 
                    id='search'
                    style={{ width: '75%' }}
                    value={query}
                    className='bg-white shadow-sm'
                    onChange={(e) => handleSearch(e.target.value)}
                    placeholder='Search'
                    autoCapitalize='none'
                    autoCorrect='none'
                    onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                            searchCustomer(query);
                        }
                    }}
                    startAdornment={
                        <InputAdornment position='start'>
                            <IconButton
                                edge='start'
                            >
                                {query ? <Search /> : null}
                            </IconButton>
                        </InputAdornment>
                    }
                    endAdornment={
                        <InputAdornment position='end'>
                            <IconButton
                                onClick={() => {
                                    handleSearch('');
                                    setSearchData([]);
                                    setError(null);
                                    dispatch(getCustomers({ page: page + 1 }));
                                }}
                                edge='end'
                            >
                                {query ? <Clear /> : null}
                            </IconButton>
                        </InputAdornment>
                    }
                />
                {error &&
                    <div style={{ padding: 10 }}>
                        <span style={{ color: 'red' }}>{error}</span>
                    </div>
                }
                {/* {query &&
                    <div className='pt-4'>
                        <Button 
                            color="primary w-25 fw-bold"
                            onClick={() => searchCustomer(query)}
                        >
                            Submit
                        </Button>
                    </div>
                } */}
            </div>
            <div>
                {/* {query && !searchData[0] && !loading && !hasMore &&
                    <div style={{ padding: 10 }}>
                        <span style={{ fontWeight: 'bold' }}>No Search Results</span>
                    </div>
                } */}
                {/* {query && loading && hasMore &&
                    <View style={{ padding: 10 }}>
                        <Text style={{ fontWeight: 'bold' }}>Searching...</Text>
                    </View>
                } */}
                {searchLoading
                ? (
                    <Loading />
                )
                : (
                    <div>
                        <div className='px-5'>
                            {(query && searchData.length ? searchData : customers).map((item: Customer) =>
                                renderItem({ item })
                            )}
                        </div>
                        {!query &&
                            <div 
                                ref={sentinelRef}
                                style={{
                                    minHeight: 20,
                                    width: '100%',
                                }}
                            />
                        }
                        {renderFooter()}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CustomerReport;